import React, { useEffect, useState, useMemo, useRef } from 'react';

export type GoogleFontFamily = string;
export type DirectionType = 'horizontal' | 'vertical' | 'diagonal';
export type AnimationType = 'none' | 'fade' | 'slide' | 'bounce' | 'glow';
export type TextTransformType =
  | 'none'
  | 'uppercase'
  | 'lowercase'
  | 'capitalize';

export interface CreativeTextProps {
  children: string;
  fontFamily?: GoogleFontFamily;
  fontSize?: number | string;
  color?: string;
  strokeColor?: string;
  strokeWidth?: number;
  shadow?:
    | boolean
    | {
        offsetX?: number;
        offsetY?: number;
        blur?: number;
        color?: string;
        opacity?: number;
      };
  gradient?: {
    colors: string[];
    direction?: DirectionType;
  };
  animation?: AnimationType;
  responsive?: boolean;
  maxWidth?: number | string;
  className?: string;
  style?: React.CSSProperties;
  onFontLoad?: () => void;
  onFontError?: (error: unknown) => void;
  fallbackFont?: string;
  letterSpacing?: number | string;
  lineHeight?: number | string;
  textTransform?: TextTransformType;
  opacity?: number;
  rotation?: number;
  skew?: { x?: number; y?: number };
}

// Simple font loading with Web Font Loader
const loadedFonts = new Set<string>();

const loadFont = async (fontFamily: string): Promise<boolean> => {
  if (!fontFamily || loadedFonts.has(fontFamily)) {
    return true;
  }

  return new Promise((resolve) => {
    // Try to load with WebFont if available
    interface WebFontWindow extends Window {
      WebFont?: {
        load: (config: {
          google: { families: string[] };
          active: () => void;
          inactive: () => void;
          timeout?: number;
        }) => void;
      };
    }
    const win = window as WebFontWindow;
    if (typeof window !== 'undefined' && win.WebFont) {
      win.WebFont.load({
        google: { families: [fontFamily] },
        active: () => {
          loadedFonts.add(fontFamily);
          resolve(true);
        },
        inactive: () => {
          resolve(false);
        },
        timeout: 5000,
      });
    } else {
      // Fallback: create link element
      const link = document.createElement('link');
      link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/\s+/g, '+')}&display=swap`;
      link.rel = 'stylesheet';

      link.onload = () => {
        loadedFonts.add(fontFamily);
        resolve(true);
      };

      link.onerror = () => resolve(false);

      document.head.appendChild(link);

      // Fallback timeout
      setTimeout(() => {
        loadedFonts.add(fontFamily);
        resolve(true);
      }, 3000);
    }
  });
};

const CreativeText: React.FC<CreativeTextProps> = ({
  children,
  fontFamily = 'Arial',
  fontSize = '24px',
  color = 'white',
  strokeColor = 'black',
  strokeWidth = 2,
  shadow = true,
  gradient,
  animation = 'none',
  responsive = true,
  maxWidth,
  className = '',
  style = {},
  onFontLoad,
  onFontError,
  fallbackFont = 'Arial, sans-serif',
  letterSpacing = 'normal',
  lineHeight = 1.2,
  opacity = 1,
  rotation = 0,
  skew = { x: 0, y: 0 },
  textTransform = 'none',
}) => {
  const [fontLoaded, setFontLoaded] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 400, height: 100 });
  const textRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Generate unique IDs for this component instance
  const instanceId = useMemo(
    () => `ct-${Math.random().toString(36).substr(2, 9)}-${Date.now()}`,
    []
  );

  const gradientId = `gradient-${instanceId}`;
  const filterId = `filter-${instanceId}`;

  // Process props
  const processedFontSize = useMemo(
    () => (typeof fontSize === 'number' ? `${fontSize}px` : fontSize),
    [fontSize]
  );

  const processedMaxWidth = useMemo(
    () => (typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth),
    [maxWidth]
  );

  const processedLetterSpacing = useMemo(
    () =>
      typeof letterSpacing === 'number' ? `${letterSpacing}px` : letterSpacing,
    [letterSpacing]
  );

  const numericFontSize = useMemo(() => {
    const match = processedFontSize.match(/(\d+\.?\d*)/);
    return match ? parseFloat(match[1]) : 24;
  }, [processedFontSize]);

  // Load font
  useEffect(() => {
    if (!fontFamily || fontFamily === 'Arial') {
      setFontLoaded(true);
      return;
    }

    loadFont(fontFamily)
      .then((success) => {
        setFontLoaded(true);
        if (success) {
          onFontLoad?.();
        } else {
          onFontError?.(new Error(`Failed to load font: ${fontFamily}`));
        }
      })
      .catch((error) => {
        setFontLoaded(true); // Still set to true to render with fallback
        onFontError?.(error);
      });
  }, [fontFamily, onFontLoad, onFontError]);

  // Calculate dimensions and layout
  useEffect(() => {
    if (!fontLoaded || !textRef.current) return;

    const measureElement = document.createElement('div');
    measureElement.style.cssText = `
      position: absolute;
      visibility: hidden;
      height: auto;
      width: auto;
      white-space: nowrap;
      font-family: "${fontFamily}", ${fallbackFont};
      font-size: ${processedFontSize};
      letter-spacing: ${processedLetterSpacing};
      text-transform: ${textTransform};
      padding: 0;
      margin: 0;
      border: 0;
      line-height: ${lineHeight};
    `;
    measureElement.textContent = children || 'Sample Text';
    document.body.appendChild(measureElement);

    const rect = measureElement.getBoundingClientRect();
    const padding = Math.max(strokeWidth * 2, 10) + 20;

    const newWidth = Math.max(rect.width + padding, 100);
    const newHeight = Math.max(rect.height + padding, numericFontSize + 20);

    setDimensions({
      width: Math.ceil(newWidth),
      height: Math.ceil(newHeight),
    });

    document.body.removeChild(measureElement);
  }, [
    fontLoaded,
    children,
    fontFamily,
    fallbackFont,
    processedFontSize,
    processedLetterSpacing,
    textTransform,
    lineHeight,
    strokeWidth,
    numericFontSize,
  ]);

  // Animation classes
  const getAnimationClass = () => {
    const animations = {
      fade: 'animate-fade-in',
      slide: 'animate-slide-in',
      bounce: 'animate-bounce',
      glow: 'animate-pulse',
      none: '',
    };
    return animations[animation] || '';
  };

  // Transform styles
  const getTransform = () => {
    const transforms: string[] = [];
    if (rotation) transforms.push(`rotate(${rotation}deg)`);
    if (skew.x) transforms.push(`skewX(${skew.x}deg)`);
    if (skew.y) transforms.push(`skewY(${skew.y}deg)`);
    return transforms.length ? transforms.join(' ') : undefined;
  };

  // Gradient definition
  const renderGradient = () => {
    if (!gradient?.colors?.length) return null;

    const { colors, direction = 'horizontal' } = gradient;
    const x1 = '0%',
      y1 = '0%';
    let x2 = '100%',
      y2 = '0%';

    if (direction === 'vertical') {
      x2 = '0%';
      y2 = '100%';
    } else if (direction === 'diagonal') {
      x2 = '100%';
      y2 = '100%';
    }

    return (
      <defs>
        <linearGradient id={gradientId} x1={x1} y1={y1} x2={x2} y2={y2}>
          {colors.map((color, index) => (
            <stop
              key={index}
              offset={`${(index / Math.max(colors.length - 1, 1)) * 100}%`}
              stopColor={color}
            />
          ))}
        </linearGradient>
      </defs>
    );
  };

  // Shadow filter
  const renderShadowFilter = () => {
    if (!shadow) return null;

    const shadowConfig =
      typeof shadow === 'boolean'
        ? {
            offsetX: 2,
            offsetY: 2,
            blur: 4,
            color: 'rgba(0,0,0,0.5)',
            opacity: 0.8,
          }
        : {
            offsetX: shadow.offsetX ?? 2,
            offsetY: shadow.offsetY ?? 2,
            blur: shadow.blur ?? 4,
            color: shadow.color ?? 'rgba(0,0,0,0.5)',
            opacity: shadow.opacity ?? 0.8,
          };

    return (
      <defs>
        <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow
            dx={shadowConfig.offsetX}
            dy={shadowConfig.offsetY}
            stdDeviation={shadowConfig.blur}
            floodColor={shadowConfig.color}
            floodOpacity={shadowConfig.opacity}
          />
        </filter>
      </defs>
    );
  };

  // Text fill color
  const textFill = gradient?.colors?.length ? `url(#${gradientId})` : color;
  const textFilter = shadow ? `url(#${filterId})` : undefined;
  const effectiveFontFamily = fontLoaded ? fontFamily : fallbackFont;

  if (!children) return null;

  return (
    <div
      ref={textRef}
      className={`creative-text-container ${getAnimationClass()} ${className}`}
      style={{
        maxWidth: processedMaxWidth || '100%',
        width: responsive ? '100%' : 'auto',
        transform: getTransform(),
        opacity,
        position: 'relative',
        display: 'inline-block',
        ...style,
      }}
    >
      <svg
        ref={svgRef}
        width={responsive ? '100%' : dimensions.width}
        height={dimensions.height}
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        style={{
          maxWidth: processedMaxWidth || '100%',
          height: 'auto',
          display: 'block',
        }}
        xmlns="http://www.w3.org/2000/svg"
      >
        {renderGradient()}
        {renderShadowFilter()}

        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily={effectiveFontFamily}
          fontSize={processedFontSize}
          fill={textFill}
          stroke={strokeWidth > 0 ? strokeColor : 'none'}
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
          strokeLinecap="round"
          filter={textFilter}
          style={{
            fontWeight: 'normal',
            userSelect: 'none',
            letterSpacing: processedLetterSpacing,
            textTransform,
            paintOrder: 'stroke fill',
          }}
        >
          {children}
        </text>
      </svg>
    </div>
  );
};

export default CreativeText;
